import {
  Component,
  Input,
  Output,
  EventEmitter,
  CUSTOM_ELEMENTS_SCHEMA,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import type { LeafMultiSelect } from '@cigna/leaf-web/lib/wc';
import '@cigna/leaf-web/lib/wc/components/multi-select/multi-select';

export type SelectedItemsChangeEvent = CustomEvent<{
  selectedItems: string[];
}>;

@Component({
  selector: 'cigna-leaf-multi-select',
  template: `
    <leaf-multi-select
      [label]="label"
      [items]="items"
      [preselectedItems]="effectivePreselectedItems"
      [fieldId]="fieldId"
      [fieldNote]="fieldNote"
      [ariaDescribedBy]="ariaDescribedBy"
      [isError]="isError"
      [errorNote]="errorNote"
      [validationAriaDescribedBy]="validationAriaDescribedBy"
      [isSuccess]="isSuccess"
      [successNote]="successNote"
      [isOptional]="isOptional"
      [optionalText]="optionalText"
      [disabled]="disabled"
      (blur)="onTouched()"
      (selectedItemsChange)="changeHandler($event)"
    ></leaf-multi-select>
  `,
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CignaLeafMultiSelectComponent),
      multi: true,
    },
  ],
})
export class CignaLeafMultiSelectComponent implements ControlValueAccessor {
  @Input({ required: true }) label: LeafMultiSelect['label'];
  @Input({ required: true }) items: LeafMultiSelect['items'];

  @Input() preselectedItems: LeafMultiSelect['preselectedItems'];
  @Input() ariaDescribedBy: LeafMultiSelect['ariaDescribedBy'];
  @Input() errorNote: LeafMultiSelect['errorNote'];
  @Input() fieldId: LeafMultiSelect['fieldId'];
  @Input() fieldNote: LeafMultiSelect['fieldNote'];
  @Input() isError: LeafMultiSelect['isError'];
  @Input() isSuccess: LeafMultiSelect['isSuccess'];
  @Input() successNote: LeafMultiSelect['successNote'];
  @Input()
  validationAriaDescribedBy: LeafMultiSelect['validationAriaDescribedBy'];
  @Input() isOptional: LeafMultiSelect['isOptional'] = false;
  @Input() optionalText: LeafMultiSelect['optionalText'];
  @Input() disabled: LeafMultiSelect['disabled'];

  @Output() selectedItemsChange = new EventEmitter<SelectedItemsChangeEvent>();

  /**
   * ********
   * CUSTOM ANGULAR PROPERTIES
   */

  // Internal state for ControlValueAccessor - tracks the form control value
  // Only used when component is bound to a form control (when writeValue has been called)
  private _formValue: string[] | null | undefined = undefined;
  // Flag to track if form control is actively managing the value
  private _isFormControlBound = false;
  onChange: (value: string[]) => void = () => {};
  onTouched: () => void = () => {};

  /**
   * Gets the value to pass to leaf component's preselectedItems.
   *
   * Behavior:
   * - If component is bound to a form control (via formControlName/formControl directive),
   *   the form control value takes precedence. null/undefined form values become [].
   * - If component is NOT bound to a form control, the preselectedItems input is used.
   *
   * This allows the component to work with and without Angular reactive forms.
   */
  get effectivePreselectedItems(): string[] | undefined {
    if (this._isFormControlBound) {
      return this._formValue ?? [];
    }
    return this.preselectedItems;
  }

  changeHandler(event: SelectedItemsChangeEvent) {
    const selectedItems = event.detail.selectedItems;
    // Update form value only if bound to form control
    if (this._isFormControlBound) {
      this._formValue = selectedItems;
    }
    this.onChange(selectedItems);
    this.selectedItemsChange.emit(event);
  }

  clearSelection() {
    if (this._isFormControlBound) {
      this._formValue = [];
      this.onChange([]);
    } else {
      this.preselectedItems = [];
    }
    // Emit an event to notify parent/component consumers
    this.selectedItemsChange.emit(
      new CustomEvent('selectedItemsChange', { detail: { selectedItems: [] } }),
    );
  }

  writeValue(value: string[] | null | undefined): void {
    this._isFormControlBound = true;
    this._formValue = value;
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
